import { useSessionTransition } from '@/stores/sessionTransition';
import { beginSessionRecovery, finishSessionRecovery, sessionPaused, sessionRealm } from '@/stores/sessionRecovery';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@valiosascrm/shared';
import { disconnectSocket } from '@/lib/socket';
import { quarantineExpiredWork, reconcileDraftOwner, useWorkDrafts } from '@/stores/workDrafts';
import { activeTenantSlug, clearLegacyLocation } from '@/lib/tenant-context';
import { useInboxStore } from '@/stores/useInboxStore';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  tenantSlug: string | null;
  // Volatile: kept in memory only for WebSocket auth, NOT persisted to localStorage
  socketToken: string | null;
  sessionRevision: number;
  setAuth: (user: User, socketToken?: string) => void;
  setUser: (user: User) => void;
  setTenantSlug: (slug: string) => void;
  setSocketToken: (token: string) => void;
  logout: () => void;
  expireSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      tenantSlug: null,
      socketToken: null,
      sessionRevision: 0,

      setAuth: (user, socketToken) => {
        const sameTenant = get().user?.id === user.id && get().user?.tenantId === user.tenantId;
        const identityChanged = !sameTenant || sessionRealm(get().user) !== sessionRealm(user);
        if (identityChanged || get().socketToken !== (socketToken || null)) disconnectSocket();
        if (identityChanged) {
          reconcileDraftOwner(user);
          useInboxStore.setState({ activeConversationId: null, searchQuery: '', isTheatreMode: false });
        }
        clearLegacyLocation();
        set({
          user,
          sessionRevision: get().sessionRevision + 1,
          isAuthenticated: true,
          socketToken: socketToken || null,
          tenantSlug: activeTenantSlug(user) || (sameTenant ? get().tenantSlug : null),
        });
      },

      setUser: (user) => {
        if (!get().user || sessionPaused()) return;
        if (get().user?.id !== user.id || get().user?.tenantId !== user.tenantId || sessionRealm(get().user) !== sessionRealm(user)) {
          beginSessionRecovery(get().user);
          return;
        }
        set({ user });
      },

      setTenantSlug: (slug) => {
        if (sessionPaused()) return;
        set({ tenantSlug: slug });
      },

      setSocketToken: (token) => {
        if (sessionPaused()) return;
        if (get().socketToken !== token) disconnectSocket();
        set({ socketToken: token });
      },

      logout: () => {
        useSessionTransition.setState({ isSwitchingTenant: false });
        finishSessionRecovery();
        clearLegacyLocation();
        useWorkDrafts.getState().clear();
        disconnectSocket();
        set({
          user: null,
          sessionRevision: get().sessionRevision + 1,
          isAuthenticated: false,
          tenantSlug: null,
          socketToken: null,
        });
      },
      expireSession: () => {
        if (sessionPaused()) return;
        useSessionTransition.setState({ isSwitchingTenant: false });
        clearLegacyLocation();
        quarantineExpiredWork(get().user);
        disconnectSocket();
        set({ sessionRevision: get().sessionRevision + 1, user: null, isAuthenticated: false, tenantSlug: null, socketToken: null });
      },
    }),
    {
      name: 'valiosascrm-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        tenantSlug: state.tenantSlug,
        // socketToken is NOT persisted — intentionally omitted
      }),
    },
  ),
);
